<template>
  <page-view :key="$route.hash" :title="$t('dashboard.title')" :noBackLink="true">
    <div class="d-f fd-c fd-md-r f-g m-b-4">
      
      <ppv-discontinued class="f-2" @dismiss="$store.dispatch('Theme/ppvDiscontinuedSeen')" v-if="!$store.getters['Theme/ppvDiscontinuedSeen'] && featuresEnabled.includes('ppv')">
        <h2>Action Required</h2>
        <p>Streamify's Pay-Per-View functionality will be discontinued on October 1, 2024. The team at Streamify is making changes to simplify our services.</p>
        <p>Please download and save any videos you have uploaded to Streamify. You must do this by September 30th.</p>
      </ppv-discontinued>

      <info-card class="f-2" icon="products" :iconCircle="true" @dismiss="$store.dispatch('Theme/liveshoppingMessageSeen')" v-if="!$store.getters['Theme/liveshoppingMessageSeen'] && featuresEnabled.includes('lvs')">
        <h2>Get started with live shopping!</h2>
        <p>This tutorial takes only a few minutes. When completed, you will have integrated Streamify with your product catalog and conducted a first test broadcast.</p>
        <p><a :href="shoppingSettings.provider === 'shopline' ? 'https://www.streamify.io/docs/shopline-getting-started-part-1-of-3.html' : 'https://www.streamify.io/docs/getting-started-part-1-of-3.html'" target="_blank">View the get started guide!</a></p>
      </info-card>
      <Onboarding class="f-1" v-if="featuresEnabled.includes('lvs')" />
    </div>
    <broadcast-list
      title="Broadcasts"
      titleLink="/broadcasts"
      titleLinkText="Show all"
      status="all"
      sortOn="time"
      direction="asc"
      :showType="true"
      :perPage="3"
      v-if="user && [1, 6, 15, 16].includes(user.permission_id)"
    />
    <div class="create-broadcast-button" v-if="user && [1, 6, 15, 16].includes(user.permission_id)">
      <Button @click="$router.push('/broadcast/schedule')" :disabled="disableNewBroadcast">Schedule new broadcast</Button>
    </div>
    <div v-if="user && [1, 6, 15, 16].includes(user.permission_id) && featuresEnabled.includes('ppv')" class="m-b-3">
      <card-minimal-title title="Revenue past month" icon="money" />
      <card-minimal>
        <div class="d-f">
          <div style="max-height: 400px" class="f-4">
            <Graph type="line" :options="revenuePastMonthOptions" :data="revenuePastMonth" :showYLabels="false" />
          </div>
          <div class="revenue-keys f-1">
            <div>
              <h3>Total transactions</h3>
              <p>{{ totalTransactions }}</p>
              <template v-if="revenuePastMonth.datasets.length > 0 && totalTransactions > 0">
                <h3>Total revenue</h3>
                <p class="currency" :style="`color: ${dataset.borderColor};`" v-for="(dataset, index) in revenuePastMonth.datasets" :key="index">{{ dataset.data.reduce((partialSum, a) => partialSum + a, 0) }} {{ dataset.label}}</p>
              </template>
            </div>
          </div>
        </div>
      </card-minimal>
    </div>
    <card-minimal-title :title="user && [1, 6, 15, 16].includes(user.permission_id) ? 'Statistics overview' : ''" :class="user && [1, 6, 15, 16].includes(user.permission_id) ? 'm-t-3' : ''" />
    <div class="d-f fd-c fd-md-r f-g fd-md-r-f-1">
      <card-minimal subtitle="Upcoming broadcasts" subtitleIcon="broadcast" class="flex">
        <div class="statInfo">
          <h2 class="statTitle">{{ upcomingBroadcastsNum }}</h2>
          <p class="statSubtitle" v-if="firstUpcomingBroadcastDate">First start {{ firstUpcomingBroadcastDate }}</p>
          <p class="statSubtitle" v-else>No upcoming broadcasts</p>
        </div>
        <div class="card-minimal-footer">
          <a class="link blue strong ta-c d-b p-a-3" href="/broadcasts" @click.prevent="$router.push('/broadcasts/upcoming')">View upcoming broadcasts</a>
        </div>
      </card-minimal>
      <card-minimal subtitle="Viewers past week" subtitleIcon="graph" class="flex" v-if="user && [1, 6, 15, 16].includes(user.permission_id)">
        <div style="max-height: 200px">
          <Graph type="bar" :data="viewersPastWeek" :showYLabels="false" />
        </div>
        <div class="card-minimal-footer">
          <p class="ta-c p-a-3">
            &nbsp;
          </p>
        </div>
      </card-minimal>
      <card-minimal subtitle="Viewers last broadcast" subtitleIcon="graph" class="flex" v-if="lastBroadcast && user && [1, 6, 15, 16].includes(user.permission_id)">
        <div class="statInfo">
          <h2 class="statTitle">{{ viewersLastBroadcast }}</h2>
          <p class="statSubtitle" v-if="lastPastBroadcast">{{ lastPastBroadcast }}</p>
          <p class="statSubtitle" v-else>No upcoming broadcasts</p>
        </div>
        <div class="card-minimal-footer">
          <a class="link blue strong d-b ta-c p-a-3" :href="`/broadcast/${lastBroadcast}`" @click.prevent="$router.push(`/broadcast/${lastBroadcast.id}`)">View last broadcast</a>
        </div>
      </card-minimal>
    </div>
    <card-minimal-title title="Articles and support" class="m-t-3" />
    <div class="d-f fd-c fd-md-r f-g fd-md-r-f-1">
      <card-minimal subtitle="Tips for Live Shopping" subtitleIcon="help" class="flex">
        <ul class="help-list">
          <li><a class="link blue" href="https://apps.apple.com/se/app/streamify-broadcaster-2/id1565790316">Download iOS app</a></li>
          <li><a class="link blue" :href="shoppingSettings.provider === 'shopline' ? 'https://www.streamify.io/docs/shopline-getting-started-part-1-of-3.html' : 'https://www.streamify.io/docs/getting-started-part-1-of-3.html'">Getting started with Live Shopping</a></li>
          <li><a class="link blue" href="https://www.streamify.io/docs/how-to-connect-streamify-with-your-shopping-cart.html">How to connect Streamify with your Shopping cart</a></li>
        </ul>
      </card-minimal>
      <card-minimal subtitle="FAQ" subtitleIcon="graph" class="flex">
        <ul class="help-list">
          <li><a class="link blue" href="https://support.streamify.io/hc/en-us/categories/115001294648-Producer-Streamify-Live-Streaming-Setup">Streamify Live Streaming Setup</a></li>
          <li><a class="link blue" href="https://streamify.zendesk.com/hc/en-us/articles/360017807374">Where can I watch my broadcast?</a></li>
        </ul>
        <div class="card-minimal-footer">
          <a href="https://support.streamify.io" class="link blue strong ta-c d-b p-a-3">More support articles</a>
        </div>
      </card-minimal>
    </div>
  </page-view>
</template>

<script>

import moment from 'moment';
import { AnalyticsService } from '@/services/analytics.service';
import BroadcastList from '@/components/Broadcast/List.vue';
import Onboarding from '@/components/Onboarding/Dashboard.vue';

export default {
  components: {
    BroadcastList,
    Onboarding,
  },
  computed: {
    disableNewBroadcast() {
      const trialEnded = this.$store.getters['AccountManager/trialEnded'];
      const subscriptionEnded = this.$store.getters['AccountManager/subscriptionEnded'];

      if (trialEnded || subscriptionEnded) {
        return true;
      }

      return false;
    },
  },
  methods: {
    async getTotalRevenue() {
      const data = await AnalyticsService.getTotalRevenueForPeriod(moment().subtract(30, 'days').format('YYYY-MM-DD'), moment().format('YYYY-MM-DD'));

      const dataLength = data.length;
      const startDate = moment().subtract(30, 'days');

      const currencies = [...new Set(data.map(item => item.currency.toLowerCase()))];
      const datasets = {};

      this.revenuePastMonthOptions.scales.y.ticks.display = false;

      // eslint-disable-next-line
      for (const [index, key] of currencies.entries()) {
        datasets[key] = JSON.parse(JSON.stringify(this.revenuePastMonthDataset));
        datasets[key].label = key.toUpperCase();
        datasets[key].backgroundColor = this.datasetColors[index];
        datasets[key].borderColor = this.datasetColors[index];
      }

      for (let i = 0; i < 30; i += 1) {
        const dateSum = {};
        // eslint-disable-next-line
        for (const key of currencies) {
          dateSum[key] = 0;
        }

        for (let t = 0; t < dataLength; t += 1) {
          if (moment(data[t].created_at).isSame(startDate, 'day')) {
            dateSum[data[t].currency.toLowerCase()] += Math.round((data[t].amount - data[t].fee) / 100);
            this.totalTransactions += 1;
          }
        }

        Object.keys(datasets).forEach((key) => {
          datasets[key].data[i] = dateSum[key];
        });

        startDate.add(1, 'days');
      }

      Object.keys(datasets).forEach((key) => {
        this.revenuePastMonth.datasets.push(datasets[key]);
      });

      if (this.totalTransactions > 0) {
        this.revenuePastMonthOptions.scales.y.ticks.display = true;
      } else {
        this.revenuePastMonth.datasets.push(this.revenuePastMonthDataset);
      }
    },
    async getPastViewers() {
      const data = await AnalyticsService.getAccountStatistics(moment().subtract(6, 'days').format('YYYY-MM-DD'));
      const visitsData = Array(7).fill().map(() => 0);

      data.forEach((event) => {
        if (event.event === 'pageview') {
          event.dataset.forEach((entry) => {
            const entryDiff = Math.floor(moment().diff(new Date(entry.date), 'hours') / 24);
            visitsData[entryDiff] += entry.value;
          });
        }
      });

      this.viewersPastWeek.datasets[0].data = visitsData.reverse();
    },
    async getLastBroadcastStats() {
      const { broadcasts } = await this.$store.dispatch('Broadcasts/getBroadcasts', {
        page: 1,
        per_page: 1,
        type: 'past',
        skipConf: true,
        skipState: true,
      });

      if (!broadcasts) {
        this.lastBroadcast = null;
        return;
      }

      if (broadcasts.length > 0) {
        const data = await AnalyticsService.getBroadcastTotalStatistics(broadcasts[0].id);
        // eslint-disable-next-line
        this.lastBroadcast = broadcasts[0];
        this.viewersLastBroadcast = data.reduce((a, event) => (event.event === 'pageview' && event.state !== 'upcoming' ? event.sum + a : a + 0), 0);

        const today = moment();
        const daDate = moment(broadcasts[0].scheduled_start_time);

        if (today.isSame(daDate, 'day')) {
          this.lastPastBroadcast = `Today at ${moment(broadcasts[0].scheduled_start_time).format('HH:mm')}`;
        } else if (moment(today.subtract(1, 'day')).isSame(daDate, 'day')) {
          this.lastPastBroadcast = `Yesterday at ${moment(broadcasts[0].scheduled_start_time).format('HH:mm')}`;
        } else if (Math.abs(today.diff(daDate, 'days')) > 1 && Math.abs(today.diff(daDate, 'days')) < 6) {
          this.lastPastBroadcast = `${moment(broadcasts[0].scheduled_start_time).format('dddd')} at ${moment(broadcasts[0].scheduled_start_time).format('HH:mm')}`;
        } else {
          this.lastPastBroadcast = `${moment(broadcasts[0].scheduled_start_time).format('DD/MM/YYYY')} at ${moment(broadcasts[0].scheduled_start_time).format('HH:mm')}`;
        }
      }
    },
    async getUpcomingBroadcasts() {
      const { broadcasts } = await this.$store.dispatch('Broadcasts/getBroadcasts', {
        page: 1,
        per_page: 100,
        type: 'upcoming',
        skipConf: true,
        skipState: true,
      });

      if (!broadcasts) {
        return;
      }

      this.upcomingBroadcastsNum = broadcasts.filter(broadcast => broadcast.status.life_cycle_status === 'ready').length;
      const [broadcast] = broadcasts.filter(bc => bc.status.life_cycle_status === 'ready');

      if (broadcast) {
        const today = moment();
        const daDate = moment(broadcast.scheduled_start_time);

        if (today.isSame(daDate, 'day')) {
          this.firstUpcomingBroadcastDate = `Today at ${moment(broadcast.scheduled_start_time).format('HH:mm')}`;
        } else if (moment(today.add(1, 'day')).isSame(daDate, 'day')) {
          this.firstUpcomingBroadcastDate = `Tomorrow at ${moment(broadcast.scheduled_start_time).format('HH:mm')}`;
        } else if (Math.abs(today.diff(daDate, 'days')) > 1 && Math.abs(today.diff(daDate, 'days')) < 6) {
          this.firstUpcomingBroadcastDate = `${moment(broadcast.scheduled_start_time).format('dddd')} at ${moment(broadcast.scheduled_start_time).format('HH:mm')}`;
        } else {
          this.firstUpcomingBroadcastDate = `${moment(broadcast.scheduled_start_time).format('DD/MM/YYYY')} at ${moment(broadcast.scheduled_start_time).format('HH:mm')}`;
        }
      }
    },
  },
  data() {
    return {
      firstUpcomingBroadcastDate: null,
      lastPastBroadcast: null,
      viewersLastBroadcast: 0,
      lastBroadcast: null,
      upcomingBroadcastsNum: 0,
      averageViewersLastBroadcast: 0,
      datasetColors: ['#0060ff', '#04CF4D', '#0000FF'],
      revenuePastMonthOptions: {
        scales: {
          y: {
            ticks: {
              display: true,
            },
          },
        },
      },
      totalTransactions: 0,
      revenuePastMonth: {
        labels: new Array(30).fill(0).map((_, index) => moment().subtract(index, 'days').format('DD/M')).reverse(),
        datasets: [],
      },
      revenuePastMonthDataset: {
        label: 'Revenue',
        data: new Array(30).fill(0),
        tension: 0.2,
        beginAtZero: true,
        pointRadius: 0,
        pointHitRadius: 20,
        total: 0,
      },
      viewersPastWeek: {
        labels: new Array(7).fill(0).map((_, index) => moment().subtract(index, 'days').format('DD/M')).reverse(),
        datasets: [
          {
            backgroundColor: '#007FFF',
            label: 'Viewers',
            data: new Array(7).fill(0),
          },
        ],
      },
    };
  },
  mounted() {
    this.getUpcomingBroadcasts();
    if ([1, 6, 15, 16].includes(this.user.permission_id)) {
      this.getPastViewers();
      this.getLastBroadcastStats();

      if (this.ppvEnabled) {
        this.getTotalRevenue();
      }
    }
  },
  filters: {
    formatSecondsToDate(value) {
      return moment(new Date(parseInt(value, 10) * 1000)).format('YYYY-MM-DD');
    },
  },
};
</script>

<style lang="scss">
.card-minimal {
  .help-list {
    padding: 0 25px;

    li {
      &:not(:last-child) {
        border-bottom: 1px solid #EEF0F9;
      }

      a {
        display: block;
        font-size: 14px;
        line-height: 22px;
        padding: 10px 0;

        @media (min-width: 768px) {
          font-size: 18px;
          line-height: 26px;
        }
      }
    }
  }

  .card-minimal-footer {
    margin: 10px 15px 0 15px;
    border-top: 1px solid #EEF0F9;
    margin-top: auto;

    a {
      font-size: 14px;

      @media (min-width: 768px) {
        font-size: 16px;
      }
    }
  }
}

.revenue-keys {
  border-left: 1px solid #EEF0F9;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 25px;

  h3 {
    font-size: 14px;
    font-weight: 500;
  }

  p {
    font-size: 50px;
    line-height: 62px;

    &.currency {
      font-size: 24px;
      line-height: 36px;
    }
  }
}
</style>

<style lang="scss">
.statInfo {
  margin: auto 0;
}
.statTitle {
  font-size: 80px;
  font-weight: 500;
  line-height: 92px;
  text-align: center;
  padding: 0 20px;

  &.blue {
    color: #0060ff;
  }

  &.green {
    color: #04CF4D,
  }
}

.statSubtitle {
  color: #868B97;
  display: block;
  font-size: 14px;
  line-height: 20px;
  text-align: center;
  width: 100%;
}

.statMore {
  border-top: 1px solid var(--color-card-inside-border);
  color: var(--color-cta-blue);
  display: block;
  font-size: 16px;
  font-weight: 500;
  line-height: 28px;
  margin: 7px 7px;
  padding: 7px 0;
  text-align: center;
}
</style>

<style lang="scss" scoped>
.create-broadcast-button {
  margin-bottom: 20px;
  padding: 10px 0 0;

  & > * {
    width: 100%;
  }

  @media (min-width: 768px) {
    margin-bottom: 25px;
    padding: 15px 0 0;
    & > * {
      width: auto;
    }
  }
}
</style>
